import { useQuery } from '@tanstack/react-query';
import { useDataChainId } from '@ui/hooks/datachain';
import type { Address } from '@ui/types/alias';

import { oraclePriceControllerApi } from '../../clients/clients';
import type {
  GetPriceUpdatesByPositionRequest,
} from '../../generated/apis/OraclePriceControllerApi';
import type { OracleBatchPriceResponse } from '../../generated/models';
import { PositionOraclePriceRequestBlockChainEnum } from '../../generated/models';
import { toServerBlockChain } from '../../utils';

type UsePriceUpdatesByPositionParams = {
  pairBase?: Address;
  collateral?: Address;
  enabled?: boolean;
  /**
   * Auto-refresh the update data while the consumer stays mounted, so the
   * signed price payload doesn't outlive the on-chain freshness bound
   * (maxDelayForTrading) before the user submits.
   */
  refetchInterval?: number;
};

export const usePriceUpdatesByPosition = ({
  pairBase,
  collateral,
  enabled = true,
  refetchInterval,
}: UsePriceUpdatesByPositionParams) => {
  const chainId = useDataChainId();

  return useQuery({
    queryKey: [
      'oraclePriceControllerApi.getPriceUpdatesByPosition',
      chainId,
      pairBase,
      collateral,
    ],
    enabled: !!pairBase && !!collateral && enabled,
    refetchInterval,
    queryFn: async (): Promise<OracleBatchPriceResponse> => {
      if (!pairBase || !collateral) {
        throw new Error('pairBase/collateral is missing');
      }

      const request: GetPriceUpdatesByPositionRequest = {
        positionOraclePriceRequest: {
          pairBase,
          collateral,
          blockChain:
            toServerBlockChain<PositionOraclePriceRequestBlockChainEnum>(chainId),
          options: {
            allowPartial: false,
            includeEncodingData: true,
            includeEncodingDataByOracleId: false,
            includeFee: true,
            includePrice: false,
            includePriceSafety: false,
            includePublishTime: false,
          },
        },
      };

      return oraclePriceControllerApi.getPriceUpdatesByPosition(request);
    },
  });
};
